/**
 * 온도 격자 → Iron Red IR 이미지용 상대 스케일.
 *
 * 권장(자동):
 *   lo = percentile(T, 0.5)
 *   hi = percentile(T, 99.5)
 *
 * 고정(리포트 통일): options 로 lo/hi 직접 지정 (render 쪽 scaleMode:'manual')
 */

/** DJI Iron Red 상대스케일 기본 퍼센타일 (0~100) */
export const IRON_RED_PERCENTILE_LOW = 0.5;
export const IRON_RED_PERCENTILE_HIGH = 99.5;

/**
 * @param {number[][]} dataMap  T[y][x] (°C), 이미지 픽셀 (x,y) 와 1:1
 * @param {{
 *   percentileLow?: number,
 *   percentileHigh?: number
 * }} [options]
 * @returns {{
 *   min: number,
 *   max: number,
 *   lo: number,
 *   hi: number,
 *   average: number,
 *   count: number,
 *   mode: 'relative',
 *   percentileLow: number,
 *   percentileHigh: number
 * } | null}
 */
export function computeRelativeTemperatureScale(dataMap, options = {}) {
  if (!Array.isArray(dataMap) || dataMap.length === 0) {
    return null;
  }

  const values = [];
  let sum = 0;
  for (let y = 0; y < dataMap.length; y += 1) {
    const row = dataMap[y];
    if (!Array.isArray(row)) continue;
    for (let x = 0; x < row.length; x += 1) {
      const v = row[x];
      if (!Number.isFinite(v)) continue;
      values.push(v);
      sum += v;
    }
  }
  if (values.length === 0) {
    return null;
  }

  const lowP = Number.isFinite(Number(options.percentileLow))
    ? Number(options.percentileLow)
    : IRON_RED_PERCENTILE_LOW;
  const highP = Number.isFinite(Number(options.percentileHigh))
    ? Number(options.percentileHigh)
    : IRON_RED_PERCENTILE_HIGH;

  values.sort((a, b) => a - b);
  let min = percentileSorted(values, lowP);
  let max = percentileSorted(values, highP);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }
  if (min === max) {
    const eps = 1e-6;
    min -= eps;
    max += eps;
  }

  return {
    min,
    max,
    lo: min,
    hi: max,
    average: sum / values.length,
    count: values.length,
    mode: 'relative',
    percentileLow: lowP,
    percentileHigh: highP
  };
}

/**
 * @param {number[]} sorted
 * @param {number} p 0..100
 */
function percentileSorted(sorted, p) {
  if (sorted.length === 1) {
    return sorted[0];
  }
  const t = (Math.max(0, Math.min(100, p)) / 100) * (sorted.length - 1);
  const i = Math.floor(t);
  const f = t - i;
  const a = sorted[i];
  const b = sorted[Math.min(i + 1, sorted.length - 1)];
  return a + (b - a) * f;
}
